import { Component, Prop, h, Host, Element, State, Method, Event, EventEmitter } from '@stencil/core';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';

@Component({
  tag: 'dda-textarea',
  styleUrls: [],
  shadow: false,
})
export class DdaTextarea {
  @Prop() label: string;
  @Prop() placeholder: string;
  @Prop() max_characters: number;
  @Prop() value: string = '';
  @Prop() input_id: string;
  @Prop() textarea_name: string;
  @Prop() aria_label?: string;
  @Prop() show_info_icon: boolean = false;
  @Prop() helper_text?: string;
  @Prop() error_message: string;
  @Prop() validation_type?: string;
  @Prop() validation_id: string;
  @Prop() input_status?: string;
  @Prop() custom_class?: string;
  @Prop() component_mode?: string;
  @Prop() enable_rich_editor?: boolean;

  @Element() el: HTMLElement;

  @State() characterCount: number = 0;
  @State() internalValue: string = '';

  private quill: Quill;

  // ✅ NEW: Events
  @Event() textareaChanged: EventEmitter<any>;
  @Event() onChanged: EventEmitter<any>;

  @Event() textareaBlurred: EventEmitter<{ name: string; value: string }>;
  @Event() onBlurred: EventEmitter<any>;

  // ✅ NEW: External value getter
  @Method()
  async getValue() {
    return this.internalValue || this.value || '';
  }

  async setValue(val: string) {
    this.internalValue = val;
    this.onChanged.emit(val);
  }

  async reset() {
    this.internalValue = '';
    // this.inputChanged.emit('');
    this.onChanged.emit('');
  }

  componentDidLoad() {
    if (this.enable_rich_editor) {
      const editor = this.el.querySelector('#editor') as HTMLElement;
      this.quill = new Quill(editor, {
        modules: {
          toolbar: [
            ['bold', 'italic', 'underline', 'strike'],
            ['blockquote', 'code-block'],
            [{ list: 'ordered' }, { list: 'bullet' }],
            [{ script: 'sub' }, { script: 'super' }],
            [{ indent: '-1' }, { indent: '+1' }],
            [{ direction: 'rtl' }],
            [{ header: [1, 2, 3, 4, 5, 6, false] }],
            [{ color: [] }, { background: [] }],
            [{ font: [] }],
            [{ align: [] }],
            ['clean'],
            ['link', 'image', 'video'],
          ],
        },
        theme: 'snow',
        placeholder: this.placeholder,
      });

      this.quill.on('text-change', () => {
        const content = this.quill.root.innerHTML;
        this.internalValue = content;
        this.characterCount = this.quill.getText().trim().length;
        this.textareaChanged.emit(); // optional
        this.onChanged.emit();
      });

      if (this.value) {
        this.quill.root.innerHTML = this.value;
        this.internalValue = this.value;
        this.characterCount = this.quill.getText().trim().length;
      }
    } else {
      this.internalValue = this.value;
      this.characterCount = this.value?.length || 0;
    }
  }

  handleInput(event: Event) {
    const val = (event.target as HTMLTextAreaElement).value;
    this.internalValue = val;
    this.characterCount = val.length;
    this.textareaChanged.emit(event);
    this.onChanged.emit(val);

    this.el.dispatchEvent(new InputEvent('input', event));
  }

  handleBlur(event: Event) {
    const val = (event.target as HTMLTextAreaElement).value.trim();
    this.textareaBlurred.emit({ name: this.textarea_name, value: val });
    this.onBlurred.emit(val);
    this.el.dispatchEvent(
      new FocusEvent('blur', {
        bubbles: true,
        composed: true,
        relatedTarget: event.target,
      }),
    );
  }

  render() {
    const textareaClass = [
      'dda-input-container dda-richtext-editor',
      this.validation_type ? `dda-validation-${this.validation_type}` : '',
      this.input_status ? `dda-input-${this.input_status}` : '',
      this.custom_class,
      this.component_mode,
      this.textarea_name,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <Host>
        <div class={textareaClass}>
          {this.label && (
            <label htmlFor={this.input_id} class="dda-input-label">
              {this.label}
            </label>
          )}

          {this.enable_rich_editor ? (
            <div id="editor" class="dda-richeditor-field"></div>
          ) : (
            <textarea
              id={this.input_id}
              name={this.textarea_name}
              aria-label={this.aria_label}
              placeholder={this.placeholder}
              value={this.internalValue || this.value}
              aria-invalid={this.validation_type === 'error' ? 'true' : 'false'}
              class="dda-input-field dda-input-textarea"
              maxLength={this.max_characters}
              onInput={event => this.handleInput(event)}
              onBlur={event => this.handleBlur(event)}
              onFocus={(event: FocusEvent) => {
                this.el.dispatchEvent(
                  new FocusEvent('focus', {
                    bubbles: true,
                    composed: true,
                    relatedTarget: event.relatedTarget,
                  }),
                );
              }}
              onKeyDown={(event: KeyboardEvent) => {
                this.el.dispatchEvent(
                  new KeyboardEvent('keydown', {
                    bubbles: true,
                    composed: true,
                    key: event.key,
                    code: event.code,
                    ctrlKey: event.ctrlKey,
                    shiftKey: event.shiftKey,
                    altKey: event.altKey,
                    metaKey: event.metaKey,
                    repeat: event.repeat,
                  }),
                );
              }}
            ></textarea>
          )}

          {this.validation_type === 'error' && this.error_message ? (
            <div class="dda-error-message" id={this.validation_id}>
              <span class="dda-d-flex dda-items-center dda-gap-2">
                {this.show_info_icon && (
                  <i class="material-icons material-symbols-outlined" aria-hidden="true">
                    info
                  </i>
                )}
                {this.error_message}
              </span>
              <span class="dda-letter-count">
                {this.characterCount} / {this.max_characters}
              </span>
            </div>
          ) : (
            <div class="dda-helper-text" id={this.validation_id}>
              <span class="dda-d-flex dda-items-center dda-gap-2">
                {this.show_info_icon && (
                  <i class="material-icons material-symbols-outlined" aria-hidden="true">
                    info
                  </i>
                )}
                {this.helper_text}
              </span>
              <span class="dda-letter-count">
                {this.characterCount} / {this.max_characters}
              </span>
            </div>
          )}
        </div>
      </Host>
    );
  }
}
